import * as Dialog from "@radix-ui/react-dialog";
import { Button } from "../core/buttons/button";
import { IconX, IconFileTypePdf, IconFileSpreadsheet, IconFileTypeCsv, IconDeviceFloppy } from "@tabler/icons-react";
import { useState } from "react";
import type { Bom } from "@/features/dashboard/types/bom";
import { useGeneratedBomCreateMutation } from "@/features/dashboard/api/queries/generated-bom-query";
import { GenerateBomForm } from "./generate-bom-form";
import { apiClient } from "@/api/api-client";

interface Props {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
}

export function GenerateBomModal({ isOpen, onClose, onSuccess }: Props) {
    const generatedBomCreateMutation = useGeneratedBomCreateMutation();
    const [projectName, setProjectName] = useState("");
    const [selectedItems, setSelectedItems] = useState<Bom[]>([]);

    const handleAddItem = (bom: Bom) => {
        setSelectedItems((prev) =>
            prev.find((i) => i.id === bom.id) ? prev : [...prev, { ...bom }]
        );
    };

    const handleRemoveItem = (id: string | null | undefined) => {
        setSelectedItems((prev) => prev.filter((item) => item.id !== id));
    };

    const handleQuantityChange = (id: string | null | undefined, qty: number) => {
        setSelectedItems((prev) =>
            prev.map((item) => {
                if (item.id !== id) return item;
                return {
                    ...item,
                    quantity: qty,
                    company_total_cost: qty * (Number(item.company_unit_cost) || 0),
                };
            })
        );
    };

    const supplierGrandTotal = selectedItems.reduce(
        (sum, item) =>
            sum + (Number(item.supplier_total_cost) || 0) * (Number(item.quantity) || 0),
        0
    );
    const companyGrandTotal = selectedItems.reduce(
        (sum, item) => sum + (Number(item.company_total_cost) || 0),
        0
    );

    // ── Export ────────────────────────────────────────────────────────────────

    const handleExport = async (format: "csv" | "xlsx" | "pdf") => {
        if (selectedItems.length === 0) return;

        const selectedIds = selectedItems.map((i) => i.id).join(",");
        const filename = `BOM_${(projectName || "Report").replace(/\s+/g, "_")}`;
        const response = await apiClient.post(
            `/bom/export/${format}`,
            { project_title: projectName, selected_ids: selectedIds },
            { responseType: "blob" }
        );
        const url = URL.createObjectURL(response.data);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${filename}.${format}`;
        a.click();
        URL.revokeObjectURL(url);
    };
// add genereate bom to generated BOM lists
    const handleGenerate = async () => {
        if (selectedItems.length === 0) return;
        await generatedBomCreateMutation.mutateAsync({
            project_name: projectName || "Untitled",
            items: selectedItems,
            item_count: selectedItems.length,
            supplier_grand_total: supplierGrandTotal,
            company_grand_total: companyGrandTotal,
        });
        onSuccess();
        handleClose();
    };

    const handleClose = () => {
        setProjectName("");
        setSelectedItems([]);
        onClose();
    };

    return (
        <Dialog.Root open={isOpen} onOpenChange={(open) => !open && handleClose()}>
            <Dialog.Portal>
                <Dialog.Overlay className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm" />
                <Dialog.Content className="fixed inset-0 z-50 bg-white flex flex-col focus:outline-none">
                    {/* Header */}
                    <div className="flex items-center justify-between px-6 py-4 border-b shrink-0">
                        <Dialog.Title className="text-xl font-semibold">
                            Generate Bill of Materials
                        </Dialog.Title>
                        <div className="flex items-center gap-2">
                            <Button
                                type="button"
                                size="sm"
                                disabled={!projectName.trim() || selectedItems.length === 0 || generatedBomCreateMutation.isPending}
                                onClick={handleGenerate}
                                className="gap-1.5"
                            >
                                <IconDeviceFloppy className="h-4 w-4" />
                                Generate
                            </Button>
                            <Dialog.Close asChild>
                                <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                    <IconX className="h-4 w-4" />
                                </Button>
                            </Dialog.Close>
                        </div>
                    </div>

                    {/* Form */}
                    <GenerateBomForm
                        projectName={projectName}
                        onProjectNameChange={setProjectName}
                        selectedItems={selectedItems}
                        onAddItem={handleAddItem}
                        onRemoveItem={handleRemoveItem}
                        onQuantityChange={handleQuantityChange}
                        supplierGrandTotal={supplierGrandTotal}
                        companyGrandTotal={companyGrandTotal}
                        isOpen={isOpen}
                    />

                    {/* Footer */}
                    <div className="flex items-center justify-between px-6 py-4 border-t bg-gray-50 shrink-0">
                        <Button type="button" variant="ghost" onClick={handleClose}>
                            Cancel
                        </Button>
                        <div className="flex gap-2">
                            <Button
                                type="button"
                                variant="outline"
                                disabled={!projectName.trim() || selectedItems.length === 0}
                                onClick={() => handleExport("csv")}
                                className="gap-1.5"
                            >
                                <IconFileTypeCsv size={16} />
                                Export as CSV
                            </Button>
                            <Button
                                type="button"
                                variant="outline"
                                disabled={!projectName.trim() || selectedItems.length === 0}
                                onClick={() => handleExport("xlsx")}
                                className="gap-1.5"
                            >
                                <IconFileSpreadsheet size={16} />
                                Export as Excel
                            </Button>
                            <Button
                                type="button"
                                disabled={!projectName.trim() || selectedItems.length === 0}
                                onClick={() => handleExport("pdf")}
                                className="gap-1.5"
                            >
                                <IconFileTypePdf size={16} />
                                Export as PDF
                            </Button>
                        </div>
                    </div>
                </Dialog.Content>
            </Dialog.Portal>
        </Dialog.Root>
    );
}
